import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { apiFetch } from './config';
import { useAuth } from './AuthContext';
import Nav from './Nav';
import EdgeSentence, { relationPhrase } from './EdgeSentence';
import { Explain } from './Explain';
import { useLensWeights, recombine } from './lensWeights';
import { extractSpine, foldFor, foldSpread, ballotFor, isQuestionNode } from './walk';
import {
  TrustBar,
  TrustBand,
  LensChips,
  ProvenanceBadge,
  SpreadTick,
  fmtScore,
} from './trustShared';
import './css/trust.css';

import { snip } from './snip';

// canonical id → cluster ({canonical, members}) from the restates collapse
const clusterMap = (data) => new Map((data.restates_clusters || []).map((c) => [c.canonical, c]));

// badge for an edge the collapse re-pointed to a canonical node
const viaBadge = (e) => {
  if (!e.via_restates) return undefined;
  const orig = e.via_restates.source?.content || e.via_restates.target?.content || '';
  return (
    <Explain concept="restates-cluster">
      <span className="trust-muted" style={{ fontSize: 11 }} title={`originally on the restatement: “${snip(orig, 160)}”`}>
        via restatement
      </span>
    </Explain>
  );
};

// provenance line under a collapsed canonical claim
function ClusterNote({ cluster, nodeId }) {
  if (!cluster) return null;
  const others = cluster.members.filter((m) => m.id !== nodeId);
  return (
    <div className="trust-muted" style={{ fontSize: 12, marginBottom: 6 }}>
      <Explain concept="restates-cluster">≈ {cluster.members.length} statements collapsed</Explain> — also stated as{' '}
      {others.slice(0, 2).map((m, i) => (
        <span key={m.id}>
          {i > 0 && ' · '}
          <Link to={`/object/${m.id}`} style={{ color: 'inherit' }}>“{snip(m.content, 90)}”</Link>
        </span>
      ))}
      {others.length > 2 && ` · +${others.length - 2} more`}
    </div>
  );
}

const CRUX = {
  factual: { color: 'var(--wv-rel-references)', label: 'FACTUAL DISPUTE' },
  values: { color: 'var(--wv-rel-derived)', label: 'VALUES DISPUTE' },
  logical: { color: 'var(--wv-rel-contains)', label: 'LOGICAL BREAK' },
  // The classifier's honest no-signal state (agenda item G, 2026-08-02).
  // Deliberately the muted border tone, not a semantic color: this pill must
  // read as "not yet determined", never as a fourth kind of dispute.
  unclassified: { color: 'var(--wv-border-strong)', label: 'UNCLASSIFIED FORK' },
};

const VALIDITY = {
  valid: { color: 'var(--wv-pos)', label: 'VALID' },
  fallacy: { color: 'var(--wv-neg)', label: 'FALLACY' },
  unverified: { color: 'var(--wv-warn)', label: 'UNVERIFIED' },
};

// A claim chip used inside divergence columns. `compact` (the Walk's mode,
// 2026-08-02 fixture round 1) drops the per-lens chip row: on the guided
// surface the fork card was ~80% lens machinery by height, which is L2
// material rendered at L0. The band and blend stay; the map keeps the chips.
function ClaimChip({ node, lenses, compact = false }) {
  return (
    <Link to={`/object/${node.id}`} className="trust-row" style={{ display: 'block', marginBottom: 8 }}>
      <div style={{ fontSize: 13, marginBottom: 6 }}>{snip(node.content, 140)}</div>
      <div style={{ marginBottom: 4 }}><TrustBar score={node.combined} width={180} /></div>
      {!compact && <LensChips lenses={lenses} scores={node.scores} size="small" />}
      {node.conflict && (
        <Explain concept="contested"><span style={{ marginLeft: 6, fontSize: 11 }} title="contested">⚡</span></Explain>
      )}
    </Link>
  );
}

// One dissonance chain: the actor-level path behind a fork, rendered as a
// votable EdgeSentence stack (DECISIONS.md 2026-07-22 — lenses detect the
// fork; the chain SHOWS it). Edge links vote; statement links narrate.
function DissonanceChain({ chain, user, onVoted }) {
  const stmt = (text) => (
    <div className="trust-row" style={{ cursor: 'default', fontSize: 14 }}>{text}</div>
  );
  return (
    <div
      className="thread-box"
      style={{ borderLeftColor: chain.viewer ? 'var(--wv-warn)' : 'var(--wv-border-strong)', marginBottom: 10 }}
    >
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
        <Explain concept="dissonance-chain">{chain.viewer ? 'Your chain' : `${chain.actor_name}'s chain`}</Explain>
        <span className="trust-muted" style={{ fontWeight: 400 }}>
          {' '}— trusts {chain.author_name}, yet endorsed what {chain.author_name}'s claim contradicts
        </span>
      </div>
      {chain.links.map((link, i) => {
        if (link.kind === 'trusts' || link.kind === 'contradicts') {
          return <EdgeSentence key={i} edge={link.edge} onVoted={onVoted} user={user} />;
        }
        if (link.kind === 'authored') {
          return (
            <div key={i}>
              {stmt(
                <>
                  <strong>{link.actor_name}</strong> authored{' '}
                  <Link to={`/object/${link.claim_id}`} style={{ color: 'inherit' }}>“{snip(link.claim_content, 120)}”</Link>
                </>
              )}
            </div>
          );
        }
        // endorsed: the human vote completing the dissonance — a vote, not an
        // edge, so there is nothing to vote ON here; it renders as testimony.
        return (
          <div key={i}>
            {stmt(
              <>
                <strong>{link.actor_name}</strong> agreed that “{snip(link.edge.source_content, 90)}”{' '}
                <strong>{relationPhrase(link.edge.relation_name, link.edge.polarity)}</strong>{' '}
                “{snip(link.edge.target_content, 90)}”
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

// A divergence (fork) card: crux, common ground, the two sides, and the
// dissonance chains that explain WHO splits and why. Used by both modes.
function DivergenceCard({ d, lenses, user, onVoted, showClaim = true, cluster, compact = false }) {
  const crux = CRUX[d.cruxType] || CRUX.unclassified;
  return (
    <div className="trust-section" style={{ marginBottom: 20, borderTop: `3px solid ${crux.color}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
        {/* under a spine step the step label already wears this pill — showing
            it twice in one viewport read as two forks (fixture round 1) */}
        {showClaim && (
          <Explain concept="crux"><span className="relation-pill" style={{ background: crux.color, margin: 0 }}>{crux.label}</span></Explain>
        )}
        <span style={{ fontSize: 13, color: 'var(--wv-text)' }}>{d.cruxReason}</span>
      </div>

      {showClaim && (
        <>
          <div style={{ fontSize: 12, color: 'var(--wv-muted)' }}>Contested claim</div>
          <div style={{ fontSize: 14, marginBottom: cluster ? 4 : 12 }}>{snip(d.claim.content)}</div>
          <ClusterNote cluster={cluster} nodeId={d.claim.id} />
        </>
      )}

      {d.sharedPremises.length > 0 && (
        <div className="thread-box" style={{ borderLeftColor: 'var(--wv-pos)', marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--wv-pos)', marginBottom: 6 }}>
            <Explain concept="common-ground">Common ground — both sides accept</Explain>
          </div>
          {d.sharedPremises.map((p) => <ClaimChip key={p.id} node={p} lenses={lenses} compact={compact} />)}
        </div>
      )}

      <div className="wv-cols">
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--wv-pos)', marginBottom: 6 }}>Supporting</div>
          {d.supporting.length ? d.supporting.map((n) => <ClaimChip key={n.id} node={n} lenses={lenses} compact={compact} />)
            : <div className="trust-muted" style={{ fontSize: 12 }}>—</div>}
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--wv-neg)', marginBottom: 6 }}>Opposing</div>
          {d.opposing.length ? d.opposing.map((n) => <ClaimChip key={n.id} node={n} lenses={lenses} compact={compact} />)
            : <div className="trust-muted" style={{ fontSize: 12 }}>—</div>}
        </div>
      </div>

      {(d.chains || []).length > 0 && (
        <div style={{ marginTop: 12, borderTop: '1px dashed var(--wv-border)', paddingTop: 10 }}>
          <div style={{ fontSize: 12, color: 'var(--wv-muted)', marginBottom: 6 }}>
            <Explain concept="dissonance-chain">Why this splits</Explain>
          </div>
          {d.chains.map((c, i) => <DissonanceChain key={i} chain={c} user={user} onVoted={onVoted} />)}
        </div>
      )}

      {d.classifications.length > 0 && (
        <div style={{ marginTop: 12, borderTop: '1px dashed var(--wv-border)', paddingTop: 10 }}>
          <div style={{ fontSize: 12, color: 'var(--wv-muted)', marginBottom: 6 }}>
            <Explain concept="crux">Crux annotations</Explain>
          </div>
          {d.classifications.map((c) => (
            <div key={c.id} style={{ fontSize: 13, marginBottom: 6 }}>
              <ProvenanceBadge activated={c.activated} upVotes={c.up_votes} downVotes={c.down_votes} />{' '}
              <Link to={`/object/${c.id}`}>{snip(c.content, 180)}</Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// The fold signal on a closed fold is count + net polarity + a lens-spread tick.
// SpreadTick moved to trustShared.js 2026-07-28 — the relation tree's collapsed
// rows render the same mark, and one shared component beats two that drift.

// One spine step: the claim, its trust band, and everything folded at this
// attachment point. Cruxes arrive pre-opened (defaultOpen). On a QUESTION
// step the candidate answers are NOT folded — they render as a visible ballot
// (2026-08-02 fixture round 1: a question is where competing answers meet,
// and "3 folded here" hid the competition that is the question's whole point).
function SpineStep({ step, label, data, spineEdgeIds, blendOf, divergence, cluster, user, onVoted, spineLabelById, inferencesById, spineContinues }) {
  const { node } = step;
  const { lenses, nodes, edges } = data;
  const byId = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes]);
  // A question is not truth-apt, so it gets NO band — and specifically not the
  // no-signal line, which would read as "nobody has voted yet" when the truth
  // is "there is nothing here to vote a score onto". Declared from the type,
  // never inferred from empty scores, for the same reason ObjectDetail declares
  // it: the two states arrive at this component looking identical.
  const question = isQuestionNode(node);
  const fold = useMemo(() => foldFor(node.id, edges, spineEdgeIds, question), [node.id, edges, spineEdgeIds, question]);
  const ballot = useMemo(
    () => (question ? ballotFor(node.id, edges, spineEdgeIds) : []),
    [question, node.id, edges, spineEdgeIds]
  );
  const spread = useMemo(() => foldSpread(node.id, fold.edges, nodes, blendOf), [node.id, fold.edges, nodes, blendOf]);
  const [open, setOpen] = useState(!!divergence);

  const hasSignal = Object.values(node.scores || {}).some((s) => s !== 0);
  const blend = blendOf(node);

  // One renderer for every attached edge — ballot rows and opened folds — so
  // the two badges live in one place: ↺ marks an edge whose far end is
  // ALREADY a spine step (a circular path made visible instead of silently
  // terminated), and an inference source unfolds its premises inline rather
  // than reading as a flat claim (the warrant arm was invisible without it).
  const edgeRow = (e) => {
    const s = byId.get(e.source);
    const t = byId.get(e.target);
    if (!s || !t) return null;
    const farId = e.source === node.id ? e.target : e.source;
    const cycleLabel =
      spineLabelById && farId !== node.id && spineLabelById.has(farId) ? spineLabelById.get(farId) : null;
    const inf = inferencesById && e.target === node.id ? inferencesById.get(e.source) : null;
    const v = inf ? VALIDITY[inf.validity] || VALIDITY.unverified : null;
    return (
      <div key={e.id}>
        <EdgeSentence
          edge={{ ...e, source_id: e.source, source_content: s.content, target_id: e.target, target_content: t.content }}
          focusId={node.id}
          onVoted={onVoted}
          onRetyped={onVoted}
          user={user}
          actions={
            <>
              {viaBadge(e)}
              {cycleLabel && (
                <span
                  className="trust-muted"
                  style={{ fontSize: 11 }}
                  title="this statement is already a step on this walk — a circular path"
                >
                  ↺ already on this walk — {cycleLabel}
                </span>
              )}
            </>
          }
        />
        {inf && (
          <div style={{ margin: '2px 0 8px 24px', fontSize: 12 }}>
            <span className="relation-pill" style={{ background: v.color, marginRight: 6 }}>{v.label}</span>
            <span className="trust-muted">an inference step — its premises:</span>
            {inf.premises.map((p) => (
              <Link key={p.id} to={`/object/${p.id}`} style={{ display: 'block', marginTop: 3, color: 'inherit', fontSize: 12 }}>
                • {snip(p.content, 120)}
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="trust-section" style={{ marginBottom: 0 }}>
      <div style={{ fontSize: 11, color: 'var(--wv-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
        <Explain concept="spine">{label}</Explain>
        {divergence && (
          <span className="relation-pill" style={{ background: (CRUX[divergence.cruxType] || CRUX.unclassified).color, marginLeft: 8 }}>
            {(CRUX[divergence.cruxType] || CRUX.unclassified).label}
          </span>
        )}
      </div>
      <Link to={`/object/${node.id}`} style={{ color: 'inherit' }}>
        <div style={{ fontSize: 15, lineHeight: 1.5, marginBottom: 8 }}>{node.content}</div>
      </Link>
      <ClusterNote cluster={cluster} nodeId={node.id} />
      {question ? (
        <div className="trust-muted" style={{ fontSize: 12, marginBottom: 6 }}>
          <Explain concept="question">not scored — a question is settled, not asserted</Explain>
        </div>
      ) : hasSignal ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <TrustBand scores={node.scores} blend={blend} width={220} />
          <Explain concept="trust-band">
            <span className="trust-muted" style={{ fontSize: 12 }}>your blend {fmtScore(blend)}</span>
          </Explain>
        </div>
      ) : (
        <div className="trust-muted" style={{ fontSize: 12, marginBottom: 6 }}>
          <Explain concept="no-signal">no lens sees this yet</Explain>
        </div>
      )}

      {ballot.length > 0 && (
        <div style={{ borderTop: '1px dashed var(--wv-border)', paddingTop: 8, marginTop: 4 }}>
          <div style={{ fontSize: 12, color: 'var(--wv-muted)', marginBottom: 6 }}>
            <Explain concept="question">
              {spineContinues
                ? `Other candidate answers (${ballot.length}) — the walk continues on the strongest-standing one below`
                : `Candidate answers (${ballot.length}) — none has standing yet, so the walk stops here`}
            </Explain>
          </div>
          {ballot.map(edgeRow)}
        </div>
      )}

      {fold.edges.length > 0 && (
        <div style={{ borderTop: '1px dashed var(--wv-border)', paddingTop: 8, marginTop: 4 }}>
          <button
            onClick={() => setOpen(!open)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--wv-muted)', fontSize: 12, padding: 0, display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
          >
            <span>{open ? '▾' : '▸'} {fold.edges.length} folded here</span>
            {fold.forCount > 0 && <span style={{ color: 'var(--wv-pos)' }}>{fold.forCount} for</span>}
            {fold.againstCount > 0 && <span style={{ color: 'var(--wv-neg)' }}>{fold.againstCount} against</span>}
            {fold.rejectedCount > 0 && (
              <span style={{ color: 'var(--wv-muted)' }} title="judged and refused by vote — these do not count as sides">
                {fold.rejectedCount} rejected
              </span>
            )}
            {fold.named.map((n) => (
              <span key={n.label} style={{ color: 'var(--wv-muted)' }}>{n.count} {n.label}</span>
            ))}
            {!open && <SpreadTick spread={spread} />}
          </button>
          <Explain concept="fold" />
          {open && (
            <div style={{ marginTop: 8 }}>
              {fold.edges.map(edgeRow)}
              {divergence && (
                <div style={{ marginTop: 10 }}>
                  <DivergenceCard d={divergence} lenses={lenses} user={user} onVoted={onVoted} showClaim={false} compact />
                </div>
              )}
            </div>
          )}
          {!open && divergence && (
            <div className="trust-muted" style={{ fontSize: 12, marginTop: 4 }}>{divergence.cruxReason}</div>
          )}
        </div>
      )}
      {fold.edges.length === 0 && divergence && (
        <div style={{ marginTop: 8 }}>
          <DivergenceCard d={divergence} lenses={lenses} user={user} onVoted={onVoted} showClaim={false} compact />
        </div>
      )}
    </div>
  );
}

// The Walk (DECISIONS.md 2026-07-22): a guided traversal of the argument —
// spine chosen by the viewer's drawer weights, conclusion-first by default,
// objections folded at their attachment points, cruxes pre-opened.
// ── the you-are-here rail (P-1, driven 2026-08-07) ─────────────────────────
//
// P-b, the rail-at-length row, was raised by the owner while driving P-1 on
// fixtures of at most four steps: a dot per step stops reading as a position
// somewhere before eighteen. Rule: every step up to the threshold, then a
// window of both ends plus three either side, ⋮ per elided run.
//
// ⚠ THE WINDOW CANNOT FIRE ON THE WALK TODAY, and pretending otherwise would
// make this decorative. `extractSpine` takes `maxSteps = 12` and loops
// `while (steps.length < maxSteps)`, so a spine is 12 steps at the absolute
// most — the owner's long-walk worry does not actually bite here. Measured at
// 12 dots the rail is ~364px against an 84vh cap, so it does not crowd either.
//
// The code stays for two reasons rather than being deleted: raising the spine
// cap is a one-argument change that would otherwise silently produce the
// cramped rail this was written to prevent, and THREADS are the surface where
// the worry is real — an authored chain has no such cap. Threshold sits at 10
// so it engages at all if the cap moves, instead of being tuned to a ceiling
// it can never cross.
const RAIL_ALL_UPTO = 10;

function railIndices(count, current) {
  if (count <= RAIL_ALL_UPTO) return Array.from({ length: count }, (_, i) => i);
  const keep = new Set([0, count - 1]);
  for (let d = -3; d <= 3; d++) {
    const j = current + d;
    if (j >= 0 && j < count) keep.add(j);
  }
  return [...keep].sort((a, b) => a - b);
}

function SpineRail({ steps, currentIndex, onJump }) {
  if (steps.length < 2) return null; // one step is not a position
  const idx = railIndices(steps.length, currentIndex);
  return (
    <nav className="walk-rail" aria-label="Walk position">
      {steps.length > RAIL_ALL_UPTO && (
        <span className="walk-rail-count">
          {currentIndex + 1}/{steps.length}
        </span>
      )}
      {idx.map((i, k) => (
        <React.Fragment key={i}>
          {k > 0 && (i === idx[k - 1] + 1 ? <span className="walk-rail-bar" /> : <span className="walk-rail-more">⋮</span>)}
          <button
            className={`walk-rail-dot${i === currentIndex ? ' cur' : ''}`}
            onClick={() => onJump(i)}
            aria-label={`Step ${i + 1}: ${steps[i].label}`}
            aria-current={i === currentIndex ? 'true' : undefined}
            title={steps[i].label}
          />
        </React.Fragment>
      ))}
      <Explain concept="walk-navigation" />
    </nav>
  );
}

function WalkView({ data, rootId, user, onVoted }) {
  const { weights } = useLensWeights();
  const [searchParams, setSearchParams] = useSearchParams();
  // order rides the URL so a shared walk arrives the way it was read
  const order = searchParams.get('order') === 'premises' ? 'premises' : 'conclusion';
  const setOrder = useCallback(
    (next) => {
      const p = new URLSearchParams(searchParams);
      if (next === 'premises') p.set('order', 'premises');
      else p.delete('order');
      setSearchParams(p, { replace: true });
    },
    [searchParams, setSearchParams]
  );
  const { nodes, edges, divergences } = data;
  const clusters = useMemo(() => clusterMap(data), [data]);

  // when the root collapsed into a restates cluster, the argument hangs off
  // its canonical — walk from there
  const startId = data.root_canonical || rootId;
  const blendOf = useCallback((n) => recombine(n.scores || {}, weights), [weights]);
  const spine = useMemo(() => extractSpine(startId, nodes, edges, blendOf), [startId, nodes, edges, blendOf]);
  const spineEdgeIds = useMemo(() => new Set(spine.map((s) => s.edge?.id).filter(Boolean)), [spine]);
  const spineNodeIds = useMemo(() => new Set(spine.map((s) => s.node.id)), [spine]);
  const divergenceAt = useMemo(() => new Map(divergences.map((d) => [d.claim.id, d])), [divergences]);
  const offSpine = divergences.filter((d) => !spineNodeIds.has(d.claim.id));
  const byId = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes]);
  const rootIsQuestion = isQuestionNode(byId.get(startId));

  // Labels follow what the step IS, not its position (see the note in the
  // loop). Precomputed as a map so any step can name any OTHER step — the ↺
  // circular-path badge needs to say where the walk has already been.
  const labelOf = (i) =>
    rootIsQuestion
      ? i === 0 ? 'question' : i === 1 ? 'answer' : `premise ${i - 1}`
      : i === 0 ? 'conclusion' : `premise ${i}`;
  const spineLabelById = useMemo(
    () => new Map(spine.map((s, i) => [s.node.id, labelOf(i)])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [spine, rootIsQuestion]
  );
  const inferencesById = useMemo(
    () => new Map((data.inferences || []).map((inf) => [inf.step.id, inf])),
    [data.inferences]
  );

  // Materialize the spine you are looking at as a THREAD (2026-08-06).
  //
  // The direction is the point. `extractSpine` walks from a conclusion DOWN to
  // its premises, so a spine reads backwards as a sequence; a thread is written
  // forwards. The `then` edges are therefore drawn from each step to the one
  // ABOVE it, which turns the walk right way round: premises first, conclusion
  // last. Doing it the other way would produce a thread whose every connector
  // pointed backwards from its own claim.
  //
  // The thread is created PRIVATE and nothing is published — the connectors are
  // ordinary inert edges, which is what every AI-suggested and hand-drawn edge
  // is until someone votes.
  const [saving, setSaving] = useState(false);
  const saveAsThread = useCallback(async () => {
    const title = window.prompt('Name this thread — it is what a shared link shows:');
    if (!title || !title.trim()) return;
    setSaving(true);
    try {
      const ordered = [...spine].reverse(); // premises → conclusion
      const res = await apiFetch('/api/threads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: title.trim(), root_id: ordered[0].node.id }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        window.alert(body.error || 'Could not save this walk');
        return;
      }
      const thread = await res.json();
      const rels = await (await apiFetch('/api/relations')).json();
      const thenId = rels.find((r) => r.name === 'then')?.id;
      if (thenId) {
        for (let i = 0; i < ordered.length - 1; i++) {
          await apiFetch('/api/relation-instances', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              relation_id: thenId,
              source_id: ordered[i].node.id,
              target_id: ordered[i + 1].node.id,
              polarity: 1,
              // Saving a walk as a thread mints the whole chain through the
              // generic door, so every connector has to say which thread it is
              // for or the saved thread opens on its root and stops there.
              thread_id: thread.id,
            }),
          });
        }
      }
      window.location.assign(`/thread/${thread.id}`);
    } finally {
      setSaving(false);
    }
  }, [spine]);

  // ── position, from SCROLL rather than from keys (driven 2026-08-07) ──────
  //
  // K-2 (arrow keys) was picked and then dropped: arrows are the one model that
  // collides with native scroll, and taking them globally breaks reading for
  // everyone who did not opt in. Dropping them carries a requirement the mockup
  // hid, because there the current step only ever moved on a keypress or a
  // click — with no keys, clicking would be the ONLY thing that moved the dot,
  // so scrolling would leave it behind and the rail would state a position that
  // is not yours. A scroll-spy is therefore not a nicety here, it is what makes
  // the rail true. It is also better than the keyboard version was: correct
  // without the reader doing anything.
  const stepRefs = useRef(new Map());
  const [currentIndex, setCurrentIndex] = useState(0);
  const jumpingTo = useRef(null);

  // Read `at` once per walk. An unknown id is IGNORED SILENTLY (per spec): a
  // shared link whose node has since been deleted, or collapsed into a restates
  // cluster, should open the walk rather than an error.
  const atParam = searchParams.get('at');
  const appliedAt = useRef(null);
  useEffect(() => {
    if (!atParam || appliedAt.current === atParam) return;
    const i = spine.findIndex((s) => s.node.id === atParam);
    appliedAt.current = atParam;
    if (i < 0) return;
    const el = stepRefs.current.get(spine[i].node.id);
    if (el) {
      jumpingTo.current = i;
      setCurrentIndex(i);
      el.scrollIntoView({ block: 'center' });
    }
  }, [atParam, spine]);

  useEffect(() => {
    const els = spine
      .map((s, i) => ({ el: stepRefs.current.get(s.node.id), i }))
      .filter((x) => x.el);
    if (els.length === 0) return;
    // A thin band across the middle of the viewport: whatever card crosses it
    // is what you are reading. Cheaper and steadier than measuring every card's
    // distance from centre on each scroll event.
    const observer = new IntersectionObserver(
      (entries) => {
        const hit = entries
          .filter((e) => e.isIntersecting)
          .map((e) => els.find((x) => x.el === e.target))
          .filter(Boolean)
          .sort((a, b) => a.i - b.i)[0];
        if (!hit) return;
        // While a programmatic jump is in flight the observer fires for every
        // card the smooth scroll passes through; honouring those would drag the
        // dot along the way and land it somewhere the user did not pick.
        if (jumpingTo.current !== null) {
          if (hit.i !== jumpingTo.current) return;
          jumpingTo.current = null;
        }
        // At the top of the page you are looking at the first step, whatever
        // happens to cross the middle band. Without this the walk opens saying
        // "step 2 of 4" with step 1 fully visible above it — measured on load,
        // 2026-08-07: a tall first card ends above the band, so the spy picked
        // the second one before the reader had scrolled at all.
        if (window.scrollY < 80) {
          setCurrentIndex(0);
          return;
        }
        setCurrentIndex(hit.i);
      },
      { rootMargin: '-45% 0px -45% 0px', threshold: 0 }
    );
    els.forEach((x) => observer.observe(x.el));
    return () => observer.disconnect();
  }, [spine, order]);

  // `at` follows the current step, with REPLACE semantics — a walk is one
  // reading, so scrolling it must not fill the back button with every card.
  useEffect(() => {
    const node = spine[currentIndex]?.node;
    if (!node) return;
    if (searchParams.get('at') === node.id) return;
    const p = new URLSearchParams(searchParams);
    p.set('at', node.id);
    appliedAt.current = node.id;
    setSearchParams(p, { replace: true });
  }, [currentIndex, spine, searchParams, setSearchParams]);

  const jumpTo = useCallback(
    (i) => {
      const node = spine[i]?.node;
      const el = node && stepRefs.current.get(node.id);
      if (!el) return;
      jumpingTo.current = i;
      setCurrentIndex(i);
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    },
    [spine]
  );

  const railSteps = useMemo(
    () => spine.map((s, i) => ({ id: s.node.id, label: labelOf(i) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [spine, rootIsQuestion]
  );

  // Interleave claim cards with the votable edge sentences connecting them,
  // then flip the whole strip for premises-first (same spine, both orders).
  const strip = [];
  spine.forEach((step, i) => {
    if (step.edge) {
      const e = step.edge;
      const s = byId.get(e.source);
      const t = byId.get(e.target);
      strip.push(
        // C-0 (owner pick 2026-08-07): NO connecting line. The rail used to be
        // a 2px border down the left of this block; the verb and its arrow are
        // the whole connector now, and vertical padding does the work the line
        // was doing — saying that these two cards belong to one another.
        <div key={`e-${e.id}`} className="walk-conn">
          <EdgeSentence
            edge={{ ...e, source_id: e.source, source_content: s?.content, target_id: e.target, target_content: t?.content }}
            onVoted={onVoted}
            user={user}
            actions={viaBadge(e)}
            // both endpoints are the adjacent cards — the connector arrow
            // points at the SUPPORTED one, which flips with the strip order
            connector={order === 'conclusion' ? '↑' : '↓'}
          />
        </div>
      );
    }
    // Labels follow what the step IS, not its position. A question root shifts
    // the whole strip by one: the root is the question, the first step is a
    // candidate answer to it, and only what hangs below THAT is a premise —
    // calling a question "conclusion" would assert exactly what it refuses to.
    strip.push(
      <div
        key={`n-${step.node.id}`}
        ref={(el) => {
          if (el) stepRefs.current.set(step.node.id, el);
          else stepRefs.current.delete(step.node.id);
        }}
        className={`walk-step${i === currentIndex ? ' cur' : ''}`}
      >
        <SpineStep
          step={step}
          label={labelOf(i)}
          data={data}
          spineEdgeIds={spineEdgeIds}
          blendOf={blendOf}
          divergence={divergenceAt.get(step.node.id)}
          cluster={clusters.get(step.node.id)}
          user={user}
          onVoted={onVoted}
          spineLabelById={spineLabelById}
          inferencesById={inferencesById}
          spineContinues={i < spine.length - 1}
        />
      </div>
    );
  });
  if (order === 'premises') strip.reverse();

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <span className="trust-muted" style={{ fontSize: 12 }}>
          <Explain concept="spine">spine chosen by <strong>your viewpoint</strong></Explain>
        </span>
        <button
          onClick={() => setOrder(order === 'conclusion' ? 'premises' : 'conclusion')}
          style={{ fontSize: 12 }}
        >
          {order === 'conclusion' ? 'conclusion first ↓' : 'premises first ↑'}
        </button>
        {/* SAVE THIS WALK AS A THREAD (2026-08-06). The spine is a chain the
            system derived from your weights and the votes; a thread is one a
            person stands behind. This is the gesture that turns one into the
            other, and it is the cheapest way for a real thread to exist —
            materializing the steps you are already looking at as `then` edges.
            Note what it does NOT do: it does not publish. */}
        {user && spine.length > 1 && (
          <button onClick={saveAsThread} disabled={saving} style={{ fontSize: 12 }}>
            {saving ? 'saving…' : 'save this walk as a thread'}
          </button>
        )}
      </div>

      {spine.length === 1 && (
        <div className="trust-muted" style={{ fontSize: 13, marginBottom: 12 }}>
          {rootIsQuestion
            ? 'No candidate answer has been put forward under this question yet — attach a claim with bears_on. Connections below.'
            : 'No activated supporting chain leads here yet — the walk is one step. Connections below.'}
        </div>
      )}

      {/* Honest state on a fresh graph (2026-07-25): the spine deliberately
          extends through inert edges (they rank below activated ones —
          DECISIONS.md 2026-07-22), but when NOTHING on it has been voted the
          walk is suggestions, not testimony — say so instead of letting the
          "strongest line of reasoning" framing overstate it. */}
      {spine.length > 1 && !spine.some((s) => s.edge && s.edge.up_votes + s.edge.down_votes > 0) && (
        <div
          className="trust-muted"
          style={{ fontSize: 13, marginBottom: 12, borderLeft: '3px solid var(--wv-warn)', paddingLeft: 10 }}
        >
          <Explain concept="activation">
            Nothing on this walk has been judged yet — every step follows an inert AI-suggested
            connection.
          </Explain>
        </div>
      )}

      <SpineRail steps={railSteps} currentIndex={currentIndex} onJump={jumpTo} />
      {strip}

      {offSpine.length > 0 && (
        <>
          <h2 style={{ fontSize: 17, marginTop: 28 }}>
            <Explain concept="spine">Forks off your spine ({offSpine.length})</Explain>
          </h2>
          {offSpine.map((d, i) => (
            <DivergenceCard key={i} d={d} lenses={data.lenses} user={user} onVoted={onVoted} cluster={clusters.get(d.claim.id)} compact />
          ))}
        </>
      )}
    </>
  );
}

// The map: the argument's full structure at once — inference steps, every
// divergence, every reasoning link. The Walk's default demoted this to a
// toggle (owner ruling 2026-07-22); nothing here was removed.
function MapView({ data, rootId, user, onVoted }) {
  const { lenses, nodes, edges, divergences, inferences } = data;
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const clusters = clusterMap(data);
  return (
    <>
      {inferences && inferences.length > 0 && (
        <>
          <h2 style={{ fontSize: 17 }}>
            <Explain concept="inference-step">Inference steps ({inferences.length})</Explain>
          </h2>
          {inferences.map((inf, i) => {
            const v = VALIDITY[inf.validity] || VALIDITY.unverified;
            return (
              <div key={i} className="trust-section" style={{ marginBottom: 14, borderLeft: `3px solid ${v.color}` }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
                  <span className="relation-pill" style={{ background: v.color, margin: 0 }}>{v.label}</span>
                  <strong style={{ fontSize: 13 }}>{inf.rule}</strong>
                  <ProvenanceBadge activated={inf.activated} upVotes={inf.up_votes} downVotes={inf.down_votes} />
                </div>
                <div style={{ fontSize: 12, color: 'var(--wv-muted)', marginBottom: 4 }}>Premises</div>
                {inf.premises.map((p) => (
                  <Link key={p.id} to={`/object/${p.id}`} style={{ display: 'block', fontSize: 13, marginBottom: 3 }}>• {snip(p.content, 120)}</Link>
                ))}
                {inf.conclusion && (
                  <>
                    <div style={{ fontSize: 18, color: v.color, margin: '2px 0' }}>∴</div>
                    <Link to={`/object/${inf.conclusion.id}`} style={{ display: 'block', fontSize: 13, fontWeight: 600 }}>
                      {snip(inf.conclusion.content, 130)}
                    </Link>
                  </>
                )}
              </div>
            );
          })}
        </>
      )}

      <h2 style={{ fontSize: 17 }}>
        <Explain concept="crux">Divergence points ({divergences.length})</Explain>
      </h2>
      {divergences.length === 0 && (
        <div className="trust-muted" style={{ fontSize: 13 }}>
          No contested fork found in this argument's reasoning subgraph yet.
        </div>
      )}
      {divergences.map((d, i) => (
        <DivergenceCard key={i} d={d} lenses={lenses} user={user} onVoted={onVoted} cluster={clusters.get(d.claim.id)} />
      ))}

      <h2 style={{ fontSize: 17, marginTop: 28 }}>
        <Explain concept="edge-sentence">Reasoning links ({edges.length})</Explain>
      </h2>
      {edges.map((e) => {
        const s = byId.get(e.source);
        const t = byId.get(e.target);
        if (!s || !t) return null;
        return (
          <EdgeSentence
            key={e.id}
            edge={{
              ...e,
              source_id: e.source,
              source_content: s.content,
              target_id: e.target,
              target_content: t.content,
            }}
            focusId={rootId}
            onVoted={onVoted}
            onRetyped={onVoted}
            user={user}
            actions={viaBadge(e)}
          />
        );
      })}
    </>
  );
}

// The Argument page. Default mode is the Walk — a guided traversal — with the
// full structure view behind a "map" toggle (DECISIONS.md 2026-07-22).
function Argument() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // mode rides the URL alongside order and at, so a walk can be linked in the
  // state it was read in (spec: mode · order · at, replace semantics).
  const [searchParams, setSearchParams] = useSearchParams();
  const mode = searchParams.get('mode') === 'map' ? 'map' : 'walk';
  const setMode = useCallback(
    (next) => {
      const p = new URLSearchParams(searchParams);
      if (next === 'map') {
        p.set('mode', 'map');
        // `at` names a spine step, and the map has no spine — carrying it over
        // would leave a parameter that points at nothing in a shared link.
        p.delete('at');
      } else {
        p.delete('mode');
      }
      setSearchParams(p, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const load = useCallback(async (withSpinner) => {
    if (withSpinner) setLoading(true);
    try {
      const res = await apiFetch(`/api/arguments/${id}/structure`);
      if (!res.ok) throw new Error('Failed to load argument structure');
      setData(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load(true);
  }, [load]);

  if (loading) return <div><Nav /><div style={{ maxWidth: 980, margin: '0 auto', padding: '0 20px' }}>Loading…</div></div>;
  if (error) return <div><Nav /><div style={{ maxWidth: 980, margin: '0 auto', padding: '0 20px', color: 'var(--wv-neg)' }}>{error}</div></div>;
  if (!data) return null;

  const onVoted = () => load(false);

  // Degenerate root (2026-07-25): the reasoning subgraph is claims-only, so
  // a source PAGE arrives as one node and zero edges — the old render was the
  // full article text as a "conclusion" card, an immobile wall of text.
  // Be honest instead, and point at the surface that IS the page's argument
  // view (the Reader). A claim with no epistemic edges gets the same frame
  // without the reader push.
  if (data.nodes.length <= 1 && data.edges.length === 0) {
    const looksLikePage = (data.root.content || '').length > 400;
    // The type is DECLARED, not inferred (2026-08-02, web-Claude research
    // session). This branch hardcoded "Claim", so a question with no reachable
    // edges announced itself as a claim — while the populated view three
    // hundred lines up gets it right from `object_type`. Two code paths, one
    // question, two answers. Same rule SpineStep already follows, and the same
    // rule ObjectDetail follows, for the same reason.
    const rootIsQuestion = isQuestionNode(data.root);
    return (
      <div className="trust-page">
        <Nav />
        <div style={{ maxWidth: 980, margin: '0 auto', padding: '0 20px 48px' }}>
          <Link to={`/object/${id}`} style={{ fontSize: 13 }}>&larr; back to node</Link>
          <h1 style={{ fontSize: 22, marginBottom: 4 }}>The Walk</h1>
          <div className="trust-section" style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, color: 'var(--wv-muted)', marginBottom: 4 }}>
              {looksLikePage ? 'Source page' : rootIsQuestion ? 'Question' : 'Claim'}
            </div>
            <div style={{ fontSize: 15, lineHeight: 1.5, marginBottom: 10 }}>{snip(data.root.content, 300)}</div>
            <div className="trust-muted" style={{ fontSize: 13 }}>
              {looksLikePage ? (
                <>
                  There is no argument ON a source page — arguments live on the claims extracted
                  from it. <Link to={`/read/${id}?d=2`}>Read it with its claims and their standing
                  arguments in place →</Link>
                </>
              ) : rootIsQuestion ? (
                // A question is not made walkable by supports/contradicts — it is
                // made walkable by candidate ANSWERS, so the claim-shaped copy was
                // not merely mistyped here, it named the wrong thing to draw.
                <>
                  No answer has been put forward under this question yet, so there is nothing to
                  walk. Attach a candidate answer from the{' '}
                  <Link to={`/object/${id}`}>object page</Link> to start one.
                </>
              ) : (data.root_attached_edges || 0) > 0 ? (
                // Honest empty state (2026-08-02, fixture round 1): connections
                // EXIST but none is a relation the walk can traverse. The old
                // copy said "nothing supports or contradicts this yet", which
                // was false about the graph — the Everest probe hit exactly
                // this on a root carrying four edges.
                <>
                  {data.root_attached_edges} connection{data.root_attached_edges === 1 ? '' : 's'} attach
                  {data.root_attached_edges === 1 ? 'es' : ''} here, but none of them is a relation the
                  walk can traverse, so there is no argument to walk yet. See everything attached on
                  the <Link to={`/object/${id}`}>object page</Link>, or draw a supports/contradicts
                  connection to start a walk.
                </>
              ) : (
                <>
                  Nothing supports or contradicts this yet, so there is no argument to walk. Draw
                  a connection from the <Link to={`/object/${id}`}>object page</Link> to start one.
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const modeBtn = (m, label) => (
    <button
      onClick={() => setMode(m)}
      style={{
        fontSize: 12,
        padding: '3px 12px',
        borderRadius: 999,
        border: '1px solid var(--wv-border-strong)',
        cursor: 'pointer',
        background: mode === m ? 'var(--wv-border-strong)' : 'none',
        color: 'var(--wv-text)',
      }}
    >
      {label}
    </button>
  );

  return (
    // nav full-bleed; the content column carries the max-width (2026-07-25)
    <div className="trust-page">
      <Nav />
      <div style={{ maxWidth: 980, margin: '0 auto', padding: '0 20px 48px' }}>
        <Link to={`/object/${id}`} style={{ fontSize: 13 }}>&larr; back to node</Link>

        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
          <h1 style={{ fontSize: 22, marginBottom: 4 }}>{mode === 'walk' ? 'The Walk' : 'The Map'}</h1>
          <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            {modeBtn('walk', 'walk')}
            {modeBtn('map', 'map')}
            <Explain concept="walk-vs-map" />
          </span>
        </div>

        {mode === 'walk' ? (
          <WalkView data={data} rootId={id} user={user} onVoted={onVoted} />
        ) : (
          <>
            <div className="trust-section" style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 12, color: 'var(--wv-muted)', marginBottom: 4 }}>Root claim</div>
              <div style={{ fontSize: 15, marginBottom: 8 }}>{data.root.content}</div>
              <Explain concept="combined-score"><TrustBar score={data.root.combined} width={220} /></Explain>
            </div>
            <MapView data={data} rootId={id} user={user} onVoted={onVoted} />
          </>
        )}
      </div>
    </div>
  );
}

export default Argument;
